// apps/provider/src/stores/copilotStore.js

import { create } from 'zustand';
import { aiService } from '../services/aiService';

export const useCopilotStore = create((set, get) => ({
  // UI state
  isOpen: false,
  isMinimized: false,
  
  // Chat state
  messages: [],
  isLoading: false,
  error: null,
  
  // Context state
  currentContext: null,
  contextHistory: [],
  
  // Suggestions
  suggestions: [],
  showSuggestions: true,

  // Actions
  toggleCopilot: () => {
    set({ isOpen: !get().isOpen });
  },

  toggleMinimized: () => {
    set({ isMinimized: !get().isMinimized });
  },

  setContext: (context) => {
    const { currentContext, contextHistory } = get();
    
    if (currentContext) {
      set({ 
        contextHistory: [...contextHistory, currentContext].slice(-10)
      });
    }
    
    set({ currentContext: context });
  },

  sendMessage: async (message) => {
    const { messages, currentContext } = get();
    
    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    
    // Create placeholder for AI message
    const aiMessageId = (Date.now() + 1).toString();
    const aiMessage = {
      id: aiMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString()
    };
    
    set({ 
      messages: [...messages, userMessage, aiMessage],
      isLoading: true,
      error: null 
    });

    try {
      // Build context for AI
      const context = {
        currentPage: window.location.pathname,
        ...currentContext,
        recentMessages: messages.slice(-5)
      };

      // Get streaming response
      const response = await aiService.getCopilotSuggestion(context, message);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      // Read the stream
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete SSE messages
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';
        
        for (const message of messages) {
          const lines = message.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.substring(6);
              
              if (data === '[DONE]') {
                set({ isLoading: false });
                return fullContent;
              }
              
              try {
                const jsonData = JSON.parse(data);
                
                if (jsonData.choices?.[0]?.delta?.content) {
                  const content = jsonData.choices[0].delta.content;
                  fullContent += content;
                  
                  // Update the AI message content in real-time
                  set(state => ({
                    messages: state.messages.map(msg => 
                      msg.id === aiMessageId 
                        ? { ...msg, content: fullContent }
                        : msg
                    )
                  }));
                }
              } catch (error) {
                console.error('Failed to parse streaming chunk:', error);
              }
            }
          }
        }
      }
      
      set({ isLoading: false });
      return fullContent;
      
    } catch (error) {
      console.error('Copilot error:', error);
      
      // Remove the empty AI message on error
      set(state => ({
        messages: state.messages.filter(msg => msg.id !== aiMessageId),
        error: error.message,
        isLoading: false
      }));
      
      throw error;
    }
  },

  clearMessages: () => {
    set({ messages: [], error: null });
  },

  generateSuggestions: async () => {
    const { currentContext } = get();
    
    if (!currentContext) return;
    
    try {
      const suggestions = await aiService.getContextualSuggestions(currentContext);
      set({ suggestions });
    } catch (error) {
      console.error('Failed to generate suggestions:', error);
    }
  },

  toggleSuggestions: () => {
    set({ showSuggestions: !get().showSuggestions });
  },

  reset: () => {
    set({
      messages: [],
      isLoading: false,
      error: null,
      currentContext: null,
      suggestions: []
    });
  }
}));